import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Target, 
  FileText, 
  Rocket,
  Check,
  Sparkles
} from 'lucide-react';
import { api } from '../services/api';

const PROJECT_CATEGORIES = [
  'General',
  'Software & Web Development',
  'AI & Machine Learning',
  'Data Engineering & Analytics',
  'Cloud & DevOps',
  'Mobile Applications',
  'Product Design & UX',
  'Business & Operations'
];

const NewProject = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    category: 'General',
    track: 'General',
    startDate: '',
    endDate: '',
    estimatedHours: '',
    teamSize: '4',
    status: 'Planning',
    progress: 0,
    skills: []
  });

  const [availableSkills, setAvailableSkills] = useState([]);

  useEffect(() => {
    let isMounted = true;
    api.getAllUniqueSkills().then(skills => {
      if (isMounted && Array.isArray(skills) && skills.length > 0) {
        setAvailableSkills(skills);
      }
    }).catch(err => {
      console.warn('Failed to pre-load skills:', err);
    });
    return () => { isMounted = false; };
  }, []);

  const [newSkill, setNewSkill] = useState({
    name: '',
    level: 'Intermediate',
    importance: 'High',
    category: 'Frontend'
  });

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  const importanceLevels = ['Low', 'Medium', 'High', 'Critical'];
  const statusOptions = ['Planning', 'In Progress', 'On Hold', 'Completed'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillNameChange = (val) => {
    const matched = availableSkills.find(s => s.name.toLowerCase() === val.trim().toLowerCase());
    setNewSkill(prev => ({
      ...prev,
      name: val,
      category: matched?.category || prev.category,
      level: matched?.level || prev.level
    }));
  };

  const handleTogglePreloadedSkill = (skillObj) => {
    const isAlreadyAdded = formData.skills.some(
      s => s.name.toLowerCase() === skillObj.name.toLowerCase()
    );
    if (isAlreadyAdded) {
      setFormData(prev => ({
        ...prev,
        skills: prev.skills.filter(s => s.name.toLowerCase() !== skillObj.name.toLowerCase())
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        skills: [
          ...prev.skills,
          {
            id: Date.now() + Math.random(),
            name: skillObj.name,
            level: skillObj.level || 'Intermediate',
            importance: 'High',
            category: skillObj.category || 'General'
          }
        ]
      }));
    }
  };

  const addSkill = (customSkill) => {
    const targetSkill = customSkill || newSkill;
    if (targetSkill.name.trim()) {
      if (formData.skills.some(s => s.name.toLowerCase() === targetSkill.name.trim().toLowerCase())) {
        return;
      }
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, { ...targetSkill, id: Date.now() + Math.random() }]
      }));
      if (!customSkill) {
        setNewSkill({ name: '', level: 'Intermediate', importance: 'High', category: 'Frontend' });
      }
    }
  };

  const removeSkill = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill.id !== skillId)
    }));
  };

  const handleOpenConfirm = (e) => {
    if (e) e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please provide a Project Name before publishing.');
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const handleConfirmPublish = async () => {
    setIsSubmitting(true);
    // Prepare the project data for storage
    const creatorName = currentUser ? currentUser.name : 'Project Member';
    const creatorEmail = currentUser ? currentUser.email : 'user@skillmesh.io';

    const projectData = {
      name: formData.name,
      tagline: formData.tagline || formData.description.slice(0, 80),
      description: formData.description,
      category: formData.category || formData.track,
      track: formData.category || formData.track,
      startDate: formData.startDate || '',
      endDate: formData.endDate || '',
      estimatedHours: formData.estimatedHours ? parseInt(formData.estimatedHours) : null,
      teamSize: parseInt(formData.teamSize) || 4,
      status: formData.status,
      creator: creatorName,
      creatorEmail: creatorEmail,
      teamMembers: [creatorName],
      teamMemberEmails: [creatorEmail],
      requiredSkillsDetails: formData.skills,
      requiredSkills: formData.skills.map(skill => skill.name)
    };

    // Add new project with unique ID
    const newProject = {
      ...projectData,
      id: `proj_${Date.now()}`,
      currentTeam: 1, // Project creator is member 1
      progress: parseInt(formData.progress) || 0
    };
    
    // Save to Neo4j database on server (and sync to cache)
    try {
      await api.createProject(newProject);
      console.log('Project persisted to server database & cache:', newProject);
    } catch (err) {
      console.warn('Fallback: saved locally due to error:', err);
    }
    
    setIsSubmitting(false);
    setIsConfirmModalOpen(false);
    // Navigate directly to the newly created project's details page so they see it immediately
    navigate(`/projects/${newProject.id}`);
  };

  return (
    <div className={`max-w-4xl mx-auto space-y-8 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors duration-200 ${
            isDarkMode 
              ? 'bg-dark-800 hover:bg-dark-700 text-white' 
              : 'bg-light-200 hover:bg-light-300 text-light-700'
          }`}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-3xl font-bold">Create New Project</h1>
            <span className="bg-primary-500/20 text-primary-400 border border-primary-500/30 text-xs px-2.5 py-1 rounded-full font-semibold flex items-center space-x-1">
              <Rocket className="h-3.5 w-3.5" />
              <span>Collaborative Project</span>
            </span>
          </div>
          <p className={`mt-1 transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            Define your project details, category, and needed skills for team matching
          </p>
        </div>
      </div>

      <form 
        onSubmit={handleOpenConfirm} 
        onKeyDown={(e) => {
          // Prevent accidental submission when hitting enter in text inputs
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
        className="space-y-8"
      >
        {/* Basic Information */}
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <FileText className={`h-5 w-5 mr-2 ${
              isDarkMode ? 'text-primary-400' : 'text-primary-600'
            }`} />
            Project Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Project Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="e.g. AI Customer Service Platform"
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Project Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
              >
                {PROJECT_CATEGORIES.map(tr => (
                  <option key={tr} value={tr}>{tr}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Elevator Pitch / Tagline
              </label>
              <input
                type="text"
                name="tagline"
                value={formData.tagline}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="A 1-sentence quick pitch of what you are building..."
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Target Team Size
              </label>
              <input
                type="number"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="4"
                min="1"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Target Due Date / Deadline (Optional)
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
              />
              <span className="text-xs text-gray-500 mt-1 block">Leave blank if ongoing or no fixed deadline.</span>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Estimated Hours / Effort (Optional)
              </label>
              <input
                type="number"
                name="estimatedHours"
                value={formData.estimatedHours}
                onChange={handleInputChange}
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="Leave blank if not applicable"
                min="1"
              />
              <span className="text-xs text-gray-500 mt-1 block">Only set if you want to track a specific hours budget.</span>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Initial Progress (%)
              </label>
              <input
                type="number"
                name="progress"
                value={formData.progress}
                onChange={handleInputChange}
                min="0"
                max="100"
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="0"
              />
            </div>
          </div>
          
          <div className="mt-6">
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-white' : 'text-light-800'
            }`}>
              Full Problem Statement & Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
              placeholder="Describe what problem you are solving, target users, tech stack ideas..."
            />
          </div>
        </div>

        {/* Required Needed Skills */}
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <h2 className="text-xl font-semibold mb-2 flex items-center">
            <Target className={`h-5 w-5 mr-2 ${
              isDarkMode ? 'text-primary-400' : 'text-primary-600'
            }`} />
            Needed Skills for Team
          </h2>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
            List the specific skills and desired proficiency levels required for teammates joining this project.
          </p>

          {/* Pre-loaded Existing Skills (1-Click Quick Select) */}
          {availableSkills.length > 0 && (
            <div className={`p-4 rounded-xl mb-6 border ${
              isDarkMode ? 'bg-dark-800/60 border-dark-700' : 'bg-light-100 border-light-200'
            }`}>
              <div className="flex items-center justify-between mb-2.5">
                <label className="text-xs font-semibold uppercase tracking-wider flex items-center space-x-1.5 text-primary-400">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Pre-loaded Organization Skills (Click to Add / Remove)</span>
                </label>
                <span className="text-[11px] text-gray-400">
                  {formData.skills.length} selected
                </span>
              </div>
              <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1">
                {availableSkills.map((sk) => {
                  const isAdded = formData.skills.some(
                    s => s.name.toLowerCase() === sk.name.toLowerCase()
                  );
                  return (
                    <button
                      key={sk.name}
                      type="button"
                      onClick={() => handleTogglePreloadedSkill(sk)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center space-x-1.5 transition-all ${
                        isAdded
                          ? 'bg-primary-600 text-white border-primary-500 shadow-md font-bold'
                          : isDarkMode
                            ? 'bg-dark-800 text-dark-300 border-dark-700 hover:border-primary-500 hover:text-white'
                            : 'bg-white text-light-700 border-light-300 hover:border-primary-500 hover:text-primary-600 shadow-sm'
                      }`}
                      title={sk.category ? `Category: ${sk.category}` : undefined}
                    >
                      {isAdded ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      <span>{sk.name}</span>
                      {sk.category && (
                        <span className={`text-[10px] opacity-75 px-1 py-0.2 rounded ${
                          isAdded ? 'bg-white/20' : isDarkMode ? 'bg-dark-700' : 'bg-light-200'
                        }`}>
                          {sk.category}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Add Custom / Specific Needed Skill */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={`block text-xs font-semibold mb-1 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Needed Skill Name <span className="text-primary-400 text-[11px] font-normal">(Autocomplete & Enter)</span>
              </label>
              <input
                type="text"
                list="existing-project-skills-list"
                value={newSkill.name}
                onChange={(e) => handleSkillNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    addSkill();
                  }
                }}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="Select existing skill or type new..."
              />
              <datalist id="existing-project-skills-list">
                {availableSkills.map((sk) => (
                  <option key={sk.name} value={sk.name}>
                    {sk.category ? `${sk.category} • ` : ''}{sk.level || 'Intermediate'}
                  </option>
                ))}
              </datalist>
            </div>
            
            <div>
              <label className={`block text-xs font-semibold mb-1 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Desired Level
              </label>
              <select
                value={newSkill.level}
                onChange={(e) => setNewSkill(prev => ({ ...prev, level: e.target.value }))}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              >
                {skillLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-xs font-semibold mb-1 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Importance
              </label>
              <select
                value={newSkill.importance}
                onChange={(e) => setNewSkill(prev => ({ ...prev, importance: e.target.value }))}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              >
                {importanceLevels.map(importance => (
                  <option key={importance} value={importance}>{importance}</option>
                ))}
              </select>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => addSkill()}
            className={`btn-secondary text-sm flex items-center space-x-1.5 py-2 px-4 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          >
            <Plus className="h-4 w-4" />
            <span>Add Needed Skill</span>
          </button>

          {/* Needed Skills List */}
          {formData.skills.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-3">Listed Needed Skills ({formData.skills.length})</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {formData.skills.map((skill) => (
                  <div key={skill.id} className={`flex items-center justify-between p-3 rounded-lg border text-sm ${
                    isDarkMode 
                      ? 'bg-dark-800 border-dark-700'
                      : 'bg-light-100 border-light-200'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <span className="font-semibold">{skill.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        isDarkMode ? 'bg-dark-700 text-white' : 'bg-light-200 text-light-800'
                      }`}>
                        {skill.level}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        skill.importance === 'Critical' ? (isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-800 border border-red-300') :
                        skill.importance === 'High' ? (isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-50 text-orange-800 border border-orange-300') :
                        skill.importance === 'Medium' ? (isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold') :
                        (isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-emerald-50 text-emerald-800 border border-emerald-300')
                      }`}>
                        {skill.importance}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSkill(skill.id)}
                      className="p-1 rounded text-red-400 hover:bg-red-500/10"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className={`flex items-center justify-between pt-6 border-t ${
          isDarkMode ? 'border-dark-800' : 'border-light-200'
        }`}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`btn-secondary ${isDarkMode ? 'dark' : 'light'}`}
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleOpenConfirm}
            className="btn-primary flex items-center space-x-2"
            disabled={!formData.name.trim()}
          >
            <Rocket className="h-4 w-4" />
            <span>Publish Project</span>
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className={`w-full max-w-lg rounded-2xl p-6 shadow-2xl border transition-all ${
            isDarkMode ? 'bg-dark-900 border-dark-700 text-white' : 'bg-white border-light-200 text-light-950'
          }`}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center">
                <Rocket className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Confirm Project Publication</h3>
                <p className="text-xs text-gray-400">Review project details before publishing to SkillMesh</p>
              </div>
            </div>

            <div className={`p-4 rounded-xl border mb-6 space-y-3 text-sm ${
              isDarkMode ? 'bg-dark-800 border-dark-700' : 'bg-light-100 border-light-200'
            }`}>
              <div>
                <span className="text-xs text-gray-400 block font-semibold uppercase tracking-wider">Project Name</span>
                <span className="font-bold text-base text-primary-400">{formData.name}</span>
              </div>

              {formData.tagline && (
                <div>
                  <span className="text-xs text-gray-400 block font-semibold uppercase tracking-wider">Tagline</span>
                  <span className="italic">{formData.tagline}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-700/30 text-xs">
                <div>
                  <span className="text-gray-400 block">Category:</span>
                  <span className="font-semibold">{formData.category}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Team Size:</span>
                  <span className="font-semibold">{formData.teamSize} members</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Status:</span>
                  <span className="font-semibold">{formData.status}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Deadline:</span>
                  <span className="font-semibold">{formData.endDate || 'Ongoing'}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block font-semibold uppercase tracking-wider mb-1">
                  Needed Skills ({formData.skills.length})
                </span>
                {formData.skills.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {formData.skills.map(s => (
                      <span key={s.id} className="text-xs px-2 py-0.5 rounded bg-primary-500/20 text-primary-300 font-medium">
                        {s.name} ({s.level})
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-amber-400 italic">No specific skills listed yet (you can add them later).</span>
                )}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300 mb-6 flex items-start space-x-2">
              <span className="text-sm">ℹ️</span>
              <span>
                <strong>Good to know:</strong> You will be able to edit all details, update required skills, and change deadlines at any time after posting.
              </span>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                disabled={isSubmitting}
                className={`btn-secondary py-2 px-4 text-sm ${isDarkMode ? 'dark' : 'light'}`}
              >
                Back to Editing
              </button>
              
              <button
                type="button"
                onClick={handleConfirmPublish}
                disabled={isSubmitting}
                className="btn-primary py-2 px-5 text-sm flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4" />
                    <span>Confirm & Publish Project</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewProject;
